import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useEvent, useUpsertEvent } from '@/hooks/useEvent';
import { deadlineInputToISO, isoToDeadlineInput } from '@/lib/event/rsvpDeadline';
import { missingWhatsAppMinimums } from '@/lib/invitation/buildWhatsAppMessage';
import { AlertTriangle } from 'lucide-react';
import DashboardSidebar from '@/components/dashboard/DashboardSidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Heart } from 'lucide-react';

/**
 * "Mi boda" — 006 US2: crear/editar el Evento (nombres de la pareja + fecha) en un
 * lugar dedicado, separado del editor de invitación. Fuente única (weddings).
 */
const WeddingSettings = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { data: event, isLoading } = useEvent();
  const upsert = useUpsertEvent();

  const [brideName, setBrideName] = useState('');
  const [groomName, setGroomName] = useState('');
  const [weddingDate, setWeddingDate] = useState('');
  const [rsvpDeadline, setRsvpDeadline] = useState('');
  const [coupleNameOrder, setCoupleNameOrder] = useState('bride_first');
  const [whatsappTemplate, setWhatsappTemplate] = useState('');
  const [coordinatorName, setCoordinatorName] = useState('');
  const [coordinatorPhone, setCoordinatorPhone] = useState('');

  useEffect(() => {
    if (!loading && !user) navigate('/auth');
  }, [user, loading, navigate]);

  // Hidrata el formulario cuando llega el evento.
  useEffect(() => {
    if (event) {
      setBrideName(event.bride_name ?? '');
      setGroomName(event.groom_name ?? '');
      setWeddingDate(event.wedding_date ? event.wedding_date.slice(0, 10) : '');
      setRsvpDeadline(isoToDeadlineInput(event.rsvp_deadline));
      setCoupleNameOrder(event.couple_name_order ?? 'bride_first');
      setWhatsappTemplate(event.whatsapp_message_template ?? '');
      setCoordinatorName(event.coordinator_name ?? '');
      setCoordinatorPhone(event.coordinator_phone ?? '');
    }
  }, [event]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    upsert.mutate({
      bride_name: brideName.trim() || null,
      groom_name: groomName.trim() || null,
      wedding_date: weddingDate ? new Date(weddingDate).toISOString() : null,
      rsvp_deadline: deadlineInputToISO(rsvpDeadline),
      couple_name_order: coupleNameOrder,
      whatsapp_message_template: whatsappTemplate.trim() || null,
      coordinator_name: coordinatorName.trim() || null,
      coordinator_phone: coordinatorPhone.trim() || null,
    });
  };

  return (
    <div className="min-h-screen flex bg-background">
      <DashboardSidebar />

      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        <main className="flex-1 overflow-y-auto">
          <div className="p-6 md:p-8 max-w-3xl mx-auto space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="font-serif text-2xl font-medium text-foreground">Mi boda</h1>
                <p className="text-sm text-muted-foreground">
                  Los nombres y la fecha son la fuente única de tu evento; se usan en el panel,
                  la invitación, el presupuesto y las tareas.
                </p>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Datos del evento</CardTitle>
                <CardDescription>Nombres de la pareja y fecha de la boda</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="bride_name">Nombre para mostrar de la novia</Label>
                    <Input
                      id="bride_name"
                      placeholder="Paola"
                      value={brideName}
                      onChange={(e) => setBrideName(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Cómo quieres que aparezca en la invitación (p. ej. “Paola”, no el nombre completo).
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="groom_name">Nombre para mostrar del novio</Label>
                    <Input
                      id="groom_name"
                      placeholder="Juan Carlos"
                      value={groomName}
                      onChange={(e) => setGroomName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="couple_name_order">Orden de los nombres</Label>
                    <select
                      id="couple_name_order"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={coupleNameOrder}
                      onChange={(e) => setCoupleNameOrder(e.target.value)}
                    >
                      <option value="bride_first">Novia primero (ej. Paola y Juan Carlos)</option>
                      <option value="groom_first">Novio primero (ej. Juan Carlos y Paola)</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="wedding_date">Fecha de la boda</Label>
                    <Input
                      id="wedding_date"
                      type="date"
                      value={weddingDate}
                      onChange={(e) => setWeddingDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rsvp_deadline">Fecha límite para confirmar (RSVP)</Label>
                    <Input
                      id="rsvp_deadline"
                      type="datetime-local"
                      value={rsvpDeadline}
                      onChange={(e) => setRsvpDeadline(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Opcional. Hora local de Bolivia. Pasada esta fecha, los invitados ya no
                      podrán cambiar su respuesta y el conteo queda cerrado.
                    </p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="coordinator_name">Coordinador/a de la boda (opcional)</Label>
                      <Input
                        id="coordinator_name"
                        placeholder="Nombre de quien coordina"
                        value={coordinatorName}
                        onChange={(e) => setCoordinatorName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="coordinator_phone">WhatsApp del/la coordinador/a</Label>
                      <Input
                        id="coordinator_phone"
                        placeholder="59171234567"
                        value={coordinatorPhone}
                        onChange={(e) => setCoordinatorPhone(e.target.value)}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground -mt-2">
                    Si configuras una fecha límite, los invitados que lleguen tarde verán un botón
                    para escribirle por WhatsApp a esta persona y coordinar su asistencia.
                  </p>

                  <div className="space-y-2">
                    <Label htmlFor="whatsapp_template">Mensaje de WhatsApp (opcional)</Label>
                    <textarea
                      id="whatsapp_template"
                      className="flex min-h-[90px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      placeholder="Déjalo vacío para usar el mensaje por defecto."
                      value={whatsappTemplate}
                      onChange={(e) => setWhatsappTemplate(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Variables: <code>{'{rotulo}'}</code> (nombre del invitado),{' '}
                      <code>{'{novios}'}</code>, <code>{'{fecha}'}</code>, <code>{'{link}'}</code>.
                      Es el texto que acompaña el link al compartir por WhatsApp. Recomendado
                      incluir al menos <code>{'{rotulo}'}</code> y <code>{'{link}'}</code>.
                    </p>
                    {(() => {
                      const wa = missingWhatsAppMinimums(whatsappTemplate);
                      if (!whatsappTemplate.trim() || (!wa.rotulo && !wa.link)) return null;
                      return (
                        <p className="text-xs text-amber-600 flex items-start gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                          <span>
                            Tu mensaje no incluye{' '}
                            {wa.rotulo && <code>{'{rotulo}'}</code>}
                            {wa.rotulo && wa.link && ' ni '}
                            {wa.link && <code>{'{link}'}</code>}.{' '}
                            {wa.link
                              ? 'Sin el link, el invitado no podrá abrir su invitación.'
                              : 'Sin el rótulo, el mensaje no llevará su nombre.'}
                          </span>
                        </p>
                      );
                    })()}
                  </div>
                  <Button type="submit" disabled={upsert.isPending || isLoading}>
                    {upsert.isPending ? 'Guardando…' : 'Guardar'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
};

export default WeddingSettings;
